import React, { Fragment, useCallback, useContext, useEffect, useRef, useMemo } from 'react';
import type { Dispatch, FC, ReactNode } from 'react';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql, useFragment } from 'react-relay';
import { styled } from '@compiled/react';
import { Subscribe } from 'unstated';

import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Inline from '@atlaskit/primitives/inline';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- existing usage; keep this PR scoped to Relay query deduplication
import Box from '@atlaskit/primitives/box';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- existing usage; keep this PR scoped to Relay query deduplication
import { xcss } from '@atlaskit/primitives/xcss';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- existing usage; keep this PR scoped to Relay query deduplication
import Flex from '@atlaskit/primitives/components/flex';

import type { PageSegmentLoadMetric } from '@atlassian/browser-metrics/page-segment-load';

import { SuspenseWithDataFetcher } from '@confluence/query-preloader-tools/entry-points/SuspenseWithDataFetcher.ts';
import { shouldShowMobileWeb } from '@confluence/mobile-detection';
import { buildContentPath } from '@confluence/breadcrumbs/entry-points/breadcrumbsHelpers.ts';
import { BreadcrumbsNonLoadable } from '@confluence/breadcrumbs/entry-points/BreadcrumbsNonLoadable.tsx';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart.ts';
import { ClassificationModalContextProvider } from '@confluence/data-classification/entry-points/ClassificationModalContext.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { AccessLinkHeaderReplacementsContext } from '@confluence/external-share-context/entry-points/AccessLinkHeaderReplacements.ts';
import { useIsAccessLinkRequest } from '@confluence/external-share-utils/entry-points/useIsAccessLinkRequest.ts';
import { useIsRestrictedExternalShareRequest } from '@confluence/external-share-utils/entry-points/useIsRestrictedExternalShareRequest.ts';
import { useResizeBanner } from '@confluence/content-types-utils/entry-points/useResizeBanner.ts';
import { isEmojiTitleEnabled } from '@confluence/content-types-utils/entry-points/isEmojiTitleEnabled.ts';
import { getContentMode } from '@confluence/content-types-utils/entry-points/getContentMode.ts';
import {
	VALID_OPERATION_TARGETS,
	isCanvasContentType,
} from '@confluence/content-types-utils/entry-points/contentTypeConstants.ts';
import type {
	ContentTypesHeaderQuery_content_nodes,
	ContentTypesHeaderQueryType,
	ContentTypesHeaderQueryVariables,
} from '@confluence/content-types-utils/entry-points/ContentTypesHeaderQuery.ts';
import type { ContentType } from '@confluence/content-types-utils/entry-points/contentTypeConstants.ts';
import {
	CONTENT_TYPES_HEADER_EXPERIENCE,
	OBJECT_HEADER_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceSuccess } from '@confluence/experience-tracker/entry-points/component.ts';
import { CopyLinkButtonLoader } from '@confluence/share-and-restrict-dialog/entry-points/CopyLink.tsx';
import { SUPPORTED_CONTENT_TYPES } from '@confluence/share-and-restrict-dialog/entry-points/useCopyContentLink.ts';
import type { ContentType as ShareContentType } from '@confluence/share-and-restrict-dialog/entry-points/ShareRestrictAndCopyCoordinator.tsx';
import { ShareAndRestrictButton } from '@confluence/share-and-restrict-dialog/entry-points/ShareAndRestrictButton.tsx';
import type {
	ShareAndRestrictPublicLinkPage,
	useShareAndRestrictButtonData_content$key,
} from '@confluence/share-and-restrict-dialog/entry-points/ShareAndRestrictButtonFragmentType.ts';
import type { EditContentButton_content$key } from '@confluence/edit-button/entry-points/EditContentButtonFragmentType.ts';
import type { ShareDialogApprovalHandoffIntent } from '@confluence/share-and-restrict-dialog/entry-points/approvalHandoff.ts';
import { useApprovals } from '@confluence/approvals/entry-points/ApprovalsContext.ts';
import {
	APPROVALS_PANE_CURRENT_CONTENT_STATUS,
	APPROVALS_PANE_DRAFT_CONTENT_STATUS,
	openApprovalsPaneForContext,
} from '@confluence/approvals/entry-points/approvalsPaneSidebar.ts';
import { useRightSidebarContext } from '@confluence/page-layout-context';
import { PageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container';
import { ContentTypesHeaderContainer } from '@confluence/content-types-header-base';
import { ArchivedPageBanner } from '@confluence/archive-pages/entry-points/ArchivedPageBannerV2.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { EmojiTitleSelection } from '@confluence/emoji-title/entry-points/EmojiTitleSelection.tsx';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { isMigratedToPanelSystem } from '@confluence/object-sidebar-api/entry-points/PanelName.ts';
import {
	useIsObjectSidebarShown,
	usePanel,
} from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { useRouteName } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useIsMobileApp } from '@confluence/mobile-app-detection/entry-points/MobileAppDetection.ts';
import { useRecapSpotlightEligibility } from '@confluence/confluence-recap/entry-points/useRecapSpotlightEligibility.ts';
import { CONTENT_HISTORY, ATTACHMENTS_PAGE } from '@confluence/named-routes/entry-points/routes.ts';
import type { WhiteboardAIAgentServiceInterface } from '@confluence/whiteboard-types/entry-points/WhiteboardAIAgentService.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import type { CanvasCollaborators } from '@confluence/team-presence/entry-points/presenceTypes.ts';
import { useAutomationUrlParamTrigger } from '@confluence/automation-menu/entry-points/useAutomationUrlParamTrigger.tsx';
import { ContentTypesHeaderQuery } from '@confluence/content-types-utils/entry-points/ContentTypesHeaderQuery.graphql.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useIsMarkdownMode } from '@confluence/content-markdown-mode/entry-points/useIsMarkdownMode.ts';

import {
	OBJECT_HEADER_SEGMENT_METRIC,
	DATABASE_HEADER_SEGMENT_METRIC,
	SMART_LINK_EMBED_HEADER_SEGMENT_METRIC,
	WHITEBOARD_HEADER_SEGMENT_METRIC,
} from './perf.config.ts';
import { ContentTypesTitle } from './ContentTypesTitle.tsx';
import type { MainActions } from './content-types-actions/MainActions.tsx';
import type { MoreActions } from './content-types-actions/MoreActions.tsx';
import type { ContentStatus } from './content-types-actions/ContentStatus/index.tsx';
import { DEFAULT_HEADER_FEATURES } from './ContentTypesHeaderFeatures.tsx';
import type { HeaderFeaturesType } from './ContentTypesHeaderFeatures.tsx';
import { useAccessLinkResolvedFeatures } from './useAccessLinkResolvedFeatures.ts';
import { getObjectHeaderBreadcrumbsContentTitle } from './getObjectHeaderBreadcrumbsContentTitle.tsx';
import { useHeaderItems } from './useHeaderItems.tsx';
import { useEditorObjectHeaderContext } from './EditorObjectHeaderContext.tsx';
import { useDisableEditorActions } from './useDisableEditorActions.tsx';
import { useProgressiveHeader } from './progressive-header/useProgressiveHeader.tsx';
import { ContentTypesTitleSubscription } from './ContentTypesTitleSubscription.tsx';
import { useContentTypesTitleUpdate } from './useContentTypesTitleUpdate.tsx';
import { ContentTypesHeaderClassification } from './object-header-items/ContentTypesHeaderClassification.tsx';
import type {
	ContentTypesHeader_content$data,
	ContentTypesHeader_content$key,
} from './__generated__/ContentTypesHeader_content.graphql.ts';
import { EditorContentTypesHeaderFragment } from './EditorContentTypesHeader.fragment.ts';
import type { EditorContentTypesHeader_content$key } from './__generated__/EditorContentTypesHeader_content.graphql.ts';
// The generated type file for ContentTypesHeaderQuery is defining the 'version' & 'subType' field as
// required but we want it to be optional because it's not always in the query. This is a
// workaround to make it optional.
export type ContentTypesHeaderQueryNode = Omit<
	ContentTypesHeaderQuery_content_nodes,
	'version' | 'subType' | 'draftVersion'
> &
	Partial<Pick<ContentTypesHeaderQuery_content_nodes, 'version' | 'subType' | 'draftVersion'>>;

type ContentTypesHeaderFragmentNode = Omit<ContentTypesHeader_content$data, ' $fragmentType'>;

type ContentTypesHeaderContentNode = ContentTypesHeaderQueryNode | ContentTypesHeaderFragmentNode;

const asContentTypesHeaderQueryNode = (
	contentData: ContentTypesHeaderContentNode | null | undefined,
): ContentTypesHeaderQueryNode | null | undefined => {
	if (!contentData) {
		return contentData;
	}

	if ('smartFolderProperties' in contentData) {
		return {
			...contentData,
			properties: contentData.smartFolderProperties,
		} as ContentTypesHeaderQueryNode;
	}

	return contentData;
};

export const ContentTypesHeaderFragment: GraphQLTaggedNode = graphql`
	fragment ContentTypesHeader_content on Content {
		id
		title
		operations {
			operation
			targetType
		}
		space {
			id
			name
			key
			alias
		}
		type
		subType
		status
		smartFolderProperties: properties(keys: ["smartFolderProperties"]) {
			nodes {
				key
				value
			}
		}
		version {
			contentTypeModified
			number
			friendlyWhen
			by {
				displayName
			}
			when
		}
		draftVersion {
			contentTypeModified
			number
			friendlyWhen
			by {
				displayName
			}
			when
		}
	}
`;

export const segmentMetricsMap: {
	[key: string]: PageSegmentLoadMetric | undefined;
} = {
	database: DATABASE_HEADER_SEGMENT_METRIC,
	embed: SMART_LINK_EMBED_HEADER_SEGMENT_METRIC,
	whiteboard: WHITEBOARD_HEADER_SEGMENT_METRIC,
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentActionContainer = styled.div<{
	showSeparator?: boolean;
}>({
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:after': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		...(props) =>
			props.showSeparator && {
				content: "''",
				border: `${token('border.width')} solid ${token('color.border')}`,
				height: '16px',
				margin: `${token('space.0')} ${token('space.050')}`,
			},
	},
});
ContentActionContainer.displayName = 'ContentActionContainer';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ArchiveBannerContainer = styled.div<{
	isFullWidth?: boolean;
}>({
	display: 'flex',
	justifyContent: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	...(props) =>
		props.isFullWidth
			? {
					margin: `0px ${token('space.200')} ${token('space.100')}`,
				}
			: {
					margin: `${token('space.negative.100')} auto 0px`,
					maxWidth: '640px',
				},
	pointerEvents: 'auto',
});
ArchiveBannerContainer.displayName = 'ArchiveBannerContainer';

const flexNavigationContainerStyles = xcss({
	alignItems: 'flex-start',
	display: 'flex',
	flexDirection: 'row',
	flexWrap: 'nowrap',
	paddingTop: 'space.0',
	paddingBottom: 'space.0',
	paddingLeft: 'space.150',
	paddingRight: 'space.150',
	boxSizing: 'border-box',
	justifyContent: 'space-between',
});

const objectHeaderFlexNavigationContainerStyles = xcss({
	paddingTop: 'space.0',
	paddingBottom: 'space.0',
	paddingLeft: 'space.0',
	paddingRight: 'space.0',
	overflow: 'hidden',
});

const floatingHeaderContainerStyles = xcss({
	marginTop: 'space.100',
	marginLeft: 'space.100',
	marginRight: 'space.100',
	backgroundColor: 'color.background.neutral.subtle',
	height: '58px',
});

const objectHeaderBreadcrumbsAndMetadataStyles = xcss({
	gap: 'space.075',
	alignItems: 'center',
	minWidth: '176px',
});

// Transition matches the one `useBreadcrumbsDisplayMode` applies to `#breadcrumbs-content-title`.
const contentTitleStyles = xcss({
	minWidth: '0',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	transition: 'opacity 100ms linear',
});

const contentTitleVisibleStyles = xcss({
	opacity: 1,
});

const contentTitleHiddenStyles = xcss({
	opacity: 0,
	pointerEvents: 'none',
});

const objectHeaderStyles = xcss({
	width: '100%',
	alignItems: 'center',
	justifyContent: 'space-between',
	backgroundColor: 'elevation.surface',
	height: '56px',
	zIndex: 'layer',
});

const objectHeaderSpotlightZIndexOverride = xcss({
	zIndex: 'spotlight',
});

// If the object header's size changes, make sure to update the height constant
// FIXED_OBJECT_HEADER_SSR_HEIGHT in contentTypesHeaderConstants.ts
const fixedBorderHeaderContainerStyles = xcss({
	padding: 'space.150',
	borderBottom: `${token('border.width')} solid ${token('color.border')}`,
});

const borderHeaderFadeInStyles = xcss({
	transition: 'border-bottom 100ms linear',
});

const fixedBorderlessHeaderContainerStyles = xcss({
	padding: 'space.150',
	borderBottom: `${token('border.width')} solid ${token('color.border.inverse')}`,
});

const floatingBlockHeaderStyles = xcss({
	borderRadius: 'radius.xlarge',
	padding: 'space.100',
	backgroundColor: 'elevation.surface',
	height: '56px',
	pointerEvents: 'auto',
	minWidth: '176px',
});

const objectHeaderActionsContainerStyles = xcss({
	flexShrink: 0,
	paddingRight: 'space.075',
});

const inlineRenameMarginStyles = xcss({
	marginLeft: 'space.negative.075',
});

const inlineRenameOverflowStyles = xcss({
	overflow: 'hidden',
});

const mobileAppWhiteboardStyles = xcss({
	pointerEvents: 'none',
	userSelect: 'none',
	touchAction: 'none',
});

const skeletonBreadcrumbsStyles = xcss({
	height: '20px',
	width: '200px',
});

const skeletonTitleStyles = xcss({
	height: '24px',
	width: '300px',
});

const skeletonActionSmallStyles = xcss({
	height: '32px',
	width: '80px',
});

const skeletonActionLargeStyles = xcss({
	height: '32px',
	width: '100px',
});

export type ContentActions = {
	mainActions: MainActions | null;
	collaborators: CanvasCollaborators | null;
	contentStatus: ContentStatus | null;
	moreActions: MoreActions | null;
};

export type ContentTypesHeaderProps = {
	// content api isn't exposed via external share. Pass the content instead for these cases
	externalShareContent?: ContentTypesHeaderQueryNode;
	contentFragmentRef?: ContentTypesHeader_content$key | null;
	// Set by the editor routes under confluence_editor_content_unified_query; null means no data.
	editorContentFragmentRef?: EditorContentTypesHeader_content$key | null;
	editContentFragmentRef?: EditContentButton_content$key | null;
	shareContentFragmentRef?: useShareAndRestrictButtonData_content$key | null;
	publicLinkPage?: ShareAndRestrictPublicLinkPage | null;
	contentId: string;
	contentActions?: ContentActions;
	isFloating?: boolean;
	allowedFeatures?: HeaderFeaturesType;
	onCloseTitleSpotlight?: () => void;
	registerShowTitleSpotlightHandler?: (callback: () => void) => void;
	handleRegisterOnShowHeaderSpotlight?: (callback: () => void) => void;
	onHeightChange?: (height: number) => void;
	isHeaderPinned?: boolean;
	isSpaceOverviewPage?: boolean;
	isConvertToFolderHighlighted?: boolean;
	setIsConvertToFolderHighlighted?: Dispatch<React.SetStateAction<boolean>>;
	whiteboardAIAgentService?: WhiteboardAIAgentServiceInterface;
	viewOnlyBadge?: React.ReactNode;
	/** Optional callback fired when the share button is clicked. */
	onShareClick?: () => void;
	/** Optional callback fired when the copy link button is clicked. */
	onCopyLinkClick?: () => void;
};

export type HeaderItems = {
	// unique key name
	name: string;
	shouldShowSeparator: boolean;
	shouldRenderInContainer?: boolean;
	shouldRender: boolean;
	children: ReactNode;
};

const getShareAction = (mode: PageMode) => (mode === PageMode.EDIT ? PageMode.EDIT : PageMode.VIEW);

const ContentTypesInlineRenameBreadcrumb = ({
	shouldShowEmojiTitle,
	contentId,
	readOnly,
	contentType,
	title,
	isMarkdownMode = false,
	spaceName,
	onCloseTitleSpotlight,
	registerShowTitleSpotlightHandler,
	showInlineRename = true,
}) => {
	const { isEditing, setUpdatedTitleFromPubsub, updateCache } = useContentTypesTitleUpdate(
		contentId,
		title,
	);
	const onDataReceivedRef = useRef<((newTitle: string) => void) | undefined>();
	const stableOnDataReceived = useMemo(
		() => (newTitle: string) => onDataReceivedRef?.current?.(newTitle),
		[],
	);

	if (!showInlineRename) {
		return null;
	}

	return (
		<Flex
			gap="space.075"
			alignItems="center"
			testId="inline-rename-breadcrumb-title"
			xcss={[inlineRenameMarginStyles, inlineRenameOverflowStyles]}
		>
			<Subscribe to={[PageTreeStateUpdater]}>
				{(pageStateUpdater: PageTreeStateUpdater) => {
					const updateContentTree = () => {
						pageStateUpdater.setForceFetchPageId(contentId);
					};

					const onDataReceived = (newTitle: string) => {
						if (!isEditing) {
							void updateCache(newTitle);
							void updateContentTree();
							setUpdatedTitleFromPubsub(undefined);
						} else {
							setUpdatedTitleFromPubsub(newTitle);
						}
					};

					onDataReceivedRef.current = onDataReceived;

					return (
						<Inline space="space.0" alignBlock="center" xcss={inlineRenameOverflowStyles}>
							{shouldShowEmojiTitle && (
								<EmojiTitleSelection
									data-testid="emoji-title-selection"
									contentId={contentId}
									isTitleDisabled={false}
									isTitleCenterAligned
									hasCoverPicture={false}
									isReadOnly={readOnly}
									contentType={contentType}
									updateContentTree={updateContentTree}
								/>
							)}
							<ContentTypesTitle
								title={title ?? ''}
								contentId={contentId}
								spaceName={spaceName ?? ''}
								readOnly={readOnly}
								key={title}
								updateContentTree={updateContentTree}
								contentType={contentType ?? ''}
								isMarkdownMode={isMarkdownMode}
								onCloseTitleSpotlight={onCloseTitleSpotlight}
								registerShowTitleSpotlightHandler={registerShowTitleSpotlightHandler}
							/>

							<ContentTypesTitleSubscription
								contentId={contentId}
								contentType={contentType}
								onDataReceived={stableOnDataReceived}
								skip={
									!fg('cc_fps_migration_content-title') ||
									!['DUAL', 'GRAPHQL'].includes(
										StatsigConfigurations.getString(
											'confluence-migrate-fps-to-graphql-subscription',
											'',
										),
									)
								}
							/>
						</Inline>
					);
				}}
			</Subscribe>
		</Flex>
	);
};
ContentTypesInlineRenameBreadcrumb.displayName = 'ContentTypesInlineRenameBreadcrumb';

type ContentTypesHeaderInnerContentProps = ContentTypesHeaderProps & {
	contentData: ContentTypesHeaderContentNode | null | undefined;
	contentTypesHeaderDataLoading?: boolean;
	ContentTypesHeaderQueryError?: Error;
};

const ContentTypesHeaderInnerContent: FC<ContentTypesHeaderInnerContentProps> = ({
	contentData,
	contentTypesHeaderDataLoading = false,
	ContentTypesHeaderQueryError,
	contentId,
	contentActions,
	isFloating = false,
	allowedFeatures = DEFAULT_HEADER_FEATURES,
	onCloseTitleSpotlight,
	registerShowTitleSpotlightHandler,
	onHeightChange,
	handleRegisterOnShowHeaderSpotlight,
	onShareClick,
	onCopyLinkClick,
	editContentFragmentRef,
	shareContentFragmentRef,
	publicLinkPage,
	isSpaceOverviewPage = false,
	isConvertToFolderHighlighted = false,
	setIsConvertToFolderHighlighted,
	whiteboardAIAgentService,
	viewOnlyBadge,
}) => {
	const { shareButton: accessLinkShareButton, title: accessLinkTitle } = useContext(
		AccessLinkHeaderReplacementsContext,
	);
	const isAccessLinkViewer = useIsAccessLinkRequest();
	const isExternal = useIsRestrictedExternalShareRequest();
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isSSR = Boolean(process.env.REACT_SSR);
	const { bannerHeight: headerHeight, setBannerElement: setHeaderHeightElement } =
		useResizeBanner(isSSR);
	const objectHeaderRef = useRef<HTMLDivElement | null>(null);
	// If accessing these values outside of editor sessions, they will be default values
	const { editorTitle, shouldRenderPreview } = useEditorObjectHeaderContext();
	const isObjectSidebarShown = useIsObjectSidebarShown();
	const rightSidebarContext = useRightSidebarContext();
	const { approvalRequest, isContentApprovalsEnabled, isRequiredApproversActive } = useApprovals();
	const panel = usePanel();
	const isLegacyObjectSidebarShown =
		isObjectSidebarShown &&
		!(fg('ads-panel-system-confluence-object-sidebar') && isMigratedToPanelSystem(panel?.id));

	const isVersionHistoryPage = useRouteName({
		selector: (routeName: string | undefined) => routeName === CONTENT_HISTORY.name,
	});

	const isAttachmentsPage = useRouteName({
		selector: (routeName: string | undefined) => routeName === ATTACHMENTS_PAGE.name,
	});

	// Automation manual trigger modal listening for query param
	useAutomationUrlParamTrigger();

	useEffect(() => {
		onHeightChange?.(headerHeight);
	}, [headerHeight, onHeightChange]);

	const contentTypesHeaderQueryNode = asContentTypesHeaderQueryNode(contentData);

	const contentSpace = contentTypesHeaderQueryNode?.space;
	const contentBasicInfo = contentTypesHeaderQueryNode;

	const spaceKey = contentSpace?.key || '';

	// Different pieces of content can have different modes, which need to be reflected in this header.
	// Eg, pages & blogposts can be 'draft', 'archived', 'view', 'edit', or 'live' (or 'embedded', or 'legacy'!)
	const rawContentType = (contentBasicInfo?.type as ShareContentType) ?? undefined; // ShareButton's shareContentType prop doesn't like null values
	const isArchived = contentBasicInfo?.status === 'archived';
	const isDraft = contentBasicInfo?.status === 'draft';
	const isViewEditOrLive = useGetPageMode();

	// we use subType value directly for live pages bc when a user is not on the live page route,
	// the live docs sweet state store is not populated and useIsCurrentPageLive in useGetPageMode returns false
	const subType = contentBasicInfo?.subType;

	const contentType = useMemo(
		() => ({
			type: rawContentType,
			mode: getContentMode(
				rawContentType,
				isArchived,
				isDraft,
				subType === 'live' ? subType : isViewEditOrLive,
			),
		}),
		[rawContentType, isArchived, isDraft, subType, isViewEditOrLive],
	);
	const hasActiveApprovalReview =
		Boolean(approvalRequest?.review) && approvalRequest?.review?.reviewStatus !== 'CANCELED';
	const approvalsPaneContentStatus = isDraft
		? APPROVALS_PANE_DRAFT_CONTENT_STATUS
		: APPROVALS_PANE_CURRENT_CONTENT_STATUS;
	const handleSuccessfulShareWithApprovalIntent = useCallback(
		(intent: ShareDialogApprovalHandoffIntent) => {
			openApprovalsPaneForContext({
				contentId,
				contentStatus: approvalsPaneContentStatus,
				initialApprovers: intent.approvers,
				entryPoint: 'shareDialog',
				rightSidebarContext,
			});
		},
		[approvalsPaneContentStatus, contentId, rightSidebarContext],
	);
	const isEditPage = useMemo(
		() =>
			contentType.mode === 'live' || contentType.mode === 'draft' || contentType.mode === 'edit',
		[contentType.mode],
	);

	const { shouldDisableEditorButtons } = useDisableEditorActions(contentId, contentType.mode);

	const isUnpublishedDraft = contentType.mode === 'draft';
	const isRecapSpotlightEligible = useRecapSpotlightEligibility(isUnpublishedDraft);
	let contentStatusContext;
	if (contentType.mode === 'live') {
		contentStatusContext = 'current-and-draft' as const;
	} else if (contentType.mode === 'edit' || contentType.mode === 'draft') {
		contentStatusContext = 'draft-only' as const;
	} else {
		contentStatusContext = 'current-only' as const;
	}

	const {
		breadcrumbsDisplayMode,
		isBorderlessHeader,
		isEditorToolbarShown,
		isTitleIntersectingWithHeader,
		shouldUseProgressiveHeader,
	} = useProgressiveHeader({
		contentType: contentType.type,
		contentMode: contentType.mode || '',
		isSpaceOverviewPage,
		isVersionHistoryPage,
		isAttachmentsPage,
		isArchived,
		isLegacyObjectSidebarShown,
		contentTypesHeaderDataLoading,
		objectHeaderRef,
		contentId,
	});

	// When the progressive reveal is off no scroll signal ever arrives, so waiting for it would
	// hide the title permanently. Show it outright instead.
	const isContentTitleVisible = isTitleIntersectingWithHeader || !shouldUseProgressiveHeader;

	const { isMobileApp } = useIsMobileApp();
	const isMobileAppWhiteboard = isMobileApp && contentType.type === 'whiteboard';

	const operations = contentData?.operations;
	const isReadOnlyDueToContext = isMobileAppWhiteboard || isExternal;
	const hasUpdatePermission = operations?.some(
		(operation) =>
			operation?.targetType &&
			VALID_OPERATION_TARGETS.includes(operation?.targetType as ContentType) &&
			operation?.operation === 'update',
	);
	const readOnly = isReadOnlyDueToContext || !hasUpdatePermission;
	const isMobile = shouldShowMobileWeb();

	const shouldShowEmojiTitle = isEmojiTitleEnabled(contentType.type);

	// Whiteboard and Database's archive banner renders in its own shell to account for a pinned solid header and a side panel
	// Similarly, pages and blogposts manage their own archive banner
	const showArchiveBanner = contentType.type === 'embed';
	const hasFullWidthBanner = contentType.type === 'embed';
	const accessLinkResolvedFeatures = useAccessLinkResolvedFeatures({
		allowedFeatures,
		hasAccessLinkTitle: Boolean(accessLinkTitle),
		isAccessLinkViewer,
	});

	const {
		showBreadcrumbs: breadcrumbsIsAllowedFeature,
		showInlineRename: inlineRenameIsAllowedFeature = true,
		showReplacementAccessLinkTitle: accessLinkTitleIsAllowedFeature = true,
	} = accessLinkResolvedFeatures;

	const shouldShowAccessLinkTitleInBreadcrumbs =
		accessLinkTitleIsAllowedFeature && Boolean(accessLinkTitle);

	const showInlineRename =
		contentType.type !== 'page' && contentType.type !== 'blogpost' && inlineRenameIsAllowedFeature;
	const isMarkdownMode = useIsMarkdownMode(contentType.type === 'page' ? contentId : undefined);
	const isLivePageInMarkdownMode =
		contentType.type === 'page' && contentType.mode === 'live' && isMarkdownMode;
	const shouldShowInlineRenameInBreadcrumbs =
		inlineRenameIsAllowedFeature && (showInlineRename || isLivePageInMarkdownMode);
	const canUpdatePageTitleInMarkdownMode = !!operations?.some(
		(operation) => operation?.targetType === 'page' && operation?.operation === 'update',
	);
	const inlineRenameReadOnly = isLivePageInMarkdownMode
		? !canUpdatePageTitleInMarkdownMode || isReadOnlyDueToContext
		: readOnly;

	const renderCopyLink = () => {
		if (
			Object.values(SUPPORTED_CONTENT_TYPES).includes(contentType.type as SUPPORTED_CONTENT_TYPES)
		) {
			return (
				// onClickCapture on a role="presentation" span intercepts copy-link clicks for
				// callers that need to observe them, without making the wrapper itself interactive.
				<span role="presentation" onClickCapture={onCopyLinkClick}>
					<CopyLinkButtonLoader
						contentId={contentId}
						spaceKey={spaceKey}
						contentType={contentType.type}
						source="contentHeader"
						/**
						 * All currently-supported content types currently treats both viewing and
						 * editing the same. To avoid issues, we'll refer to viewing to maintain
						 * consistency, as using 'edit' would generate a different URL.
						 */
						pageMode="view"
					/>
				</span>
			);
		}
	};

	const renderShare = (opts?: { isNotifyDisabled?: boolean }) => {
		// `ShareAndRestrictButton` renders permanently disabled for a token-only viewer, whose
		// permissions query never resolves.
		if (isAccessLinkViewer && accessLinkShareButton) {
			return (
				// onClickCapture on a role="presentation" span intercepts share clicks for
				// callers that need to observe them, without making the wrapper itself interactive.
				<span role="presentation" onClickCapture={onShareClick}>
					{accessLinkShareButton}
				</span>
			);
		}

		return (
			// onClickCapture on a role="presentation" span intercepts share clicks for
			// callers that need to observe them, without making the wrapper itself interactive.
			<span role="presentation" onClickCapture={onShareClick}>
				<ShareAndRestrictButton
					contentId={contentId}
					contentFragmentRef={shareContentFragmentRef}
					publicLinkPage={publicLinkPage}
					contentType={contentType.type}
					shareeAction={getShareAction(isViewEditOrLive)}
					source="contentHeader"
					isUnpublishedDraft={contentType.mode === 'draft'}
					contentSubType={contentType.mode === 'live' ? 'live' : undefined}
					isNotifyDisabled={opts?.isNotifyDisabled}
					isApprovalHandoffEnabled={
						contentType.type === 'page' &&
						contentType.mode !== 'live' &&
						isContentApprovalsEnabled &&
						!hasActiveApprovalReview &&
						!isRequiredApproversActive &&
						expValEquals('confluence_approvals_eap', 'isEnabled', true)
					}
					onSuccessfulShareWithApprovalIntent={handleSuccessfulShareWithApprovalIntent}
				/>
			</span>
		);
	};

	const wrappedRenderShare = (opts?: { isNotifyDisabled?: boolean }) => {
		return (
			<>
				<Inline>{renderShare(opts)}</Inline>
			</>
		);
	};

	// The primary reason for this structure is to support programmatic control of when a separator should render
	const headerItems: HeaderItems[] = useHeaderItems({
		contentId,
		contentType: contentType.type,
		contentMode: contentType.mode,
		contentActions,
		handleRegisterOnShowHeaderSpotlight,
		allowedFeatures: accessLinkResolvedFeatures,
		contentData: contentTypesHeaderQueryNode,
		editContentFragmentRef,
		renderCopyLink,
		renderShare: wrappedRenderShare,
		isSpaceOverviewPage,
		readOnly,
		isConvertToFolderHighlighted,
		setIsConvertToFolderHighlighted,
		whiteboardAIAgentService,
		isVersionHistoryPage,
		isAttachmentsPage,
	});

	const renderActionContainers = () => {
		const contentToRender = headerItems.filter((contentActions) => contentActions.shouldRender);

		return contentToRender.map((contentAction, index) => {
			const { name, shouldShowSeparator, children, shouldRenderInContainer = true } = contentAction;
			const isLastEntry = index + 1 === contentToRender.length;
			const showSeparator = isLastEntry ? false : shouldShowSeparator;
			const testIdName = `${name}-action-container-${
				showSeparator ? 'with-separator' : 'without-separator'
			}`;
			return (
				<Fragment key={name}>
					{shouldRenderInContainer ? (
						<ContentActionContainer
							data-testid={testIdName}
							showSeparator={showSeparator}
							data-vc={testIdName}
						>
							{children}
						</ContentActionContainer>
					) : (
						children
					)}
				</Fragment>
			);
		});
	};

	const hasActionsToRender = () => {
		return headerItems.some((contentAction) => contentAction.shouldRender);
	};

	const renderObjectHeader = () => {
		if (shouldRenderPreview) return null;

		const isFloatingBlockHeader = isCanvasContentType(contentType.type);

		// Return link for Version History or Attachments breadcrumbs
		const contentLink =
			contentId && contentType.type && (isVersionHistoryPage || isAttachmentsPage)
				? buildContentPath({
						contentId,
						contentType: contentType.type === 'space' ? 'page' : contentType.type,
						isSpaceOverview: isSpaceOverviewPage,
						spaceKey,
					})
				: undefined;

		return (
			<ErrorBoundary attribution={Attribution.DISCO}>
				<Flex
					xcss={[
						objectHeaderStyles,
						isRecapSpotlightEligible && objectHeaderSpotlightZIndexOverride,
						isFloatingBlockHeader && floatingHeaderContainerStyles,
						!isFloatingBlockHeader && isBorderlessHeader && fixedBorderlessHeaderContainerStyles,
						!isFloatingBlockHeader && !isBorderlessHeader && fixedBorderHeaderContainerStyles,
						!isEditorToolbarShown && borderHeaderFadeInStyles,
						isMobileAppWhiteboard && mobileAppWhiteboardStyles,
					]}
					ref={objectHeaderRef}
					id="object-header-container-id"
					testId="object-header-container"
					data-vc="object-header-container"
				>
					<Flex
						xcss={[
							objectHeaderBreadcrumbsAndMetadataStyles,
							isFloatingBlockHeader && floatingBlockHeaderStyles,
						]}
					>
						{breadcrumbsIsAllowedFeature && (
							<BreadcrumbsNonLoadable
								contentId={contentId}
								// Use the space key alias if it exists, otherwise use the space key.  We want to use
								// the alias because that is used in the route URL so SSR will pick that up as the
								// space key and use it in the preloading cache for the space detail query.  If we
								// use the true (UUID) space key here in the request, the SSR cache will not find it
								// in the cache and we won't see the breadcrumbs in the SSR'ed version of the header
								spaceKey={contentSpace?.alias ?? contentSpace?.key ?? ''}
								contentTitle={getObjectHeaderBreadcrumbsContentTitle({
									isSpaceOverviewPage,
									contentMode: contentType.mode,
									editorTitle,
									contentTitle: contentBasicInfo?.title,
								})}
								showInlineRename={shouldShowInlineRenameInBreadcrumbs}
								shouldDisableMoveButton={shouldDisableEditorButtons}
								displayMode={breadcrumbsDisplayMode}
								contentLink={contentLink}
								contentType={contentType.type}
								subType={contentType.mode === 'live' ? 'live' : undefined}
							/>
						)}
						{/* Fades in with the same signal as the breadcrumb title it replaces. */}
						{shouldShowAccessLinkTitleInBreadcrumbs && (
							<Box
								xcss={[
									contentTitleStyles,
									isContentTitleVisible ? contentTitleVisibleStyles : contentTitleHiddenStyles,
								]}
								testId="header-content-title"
							>
								{accessLinkTitle}
							</Box>
						)}
						{/* only render this for content types because the object header is used for pages and blogs too which don't have inline rename breadcrumbs yet */}
						{shouldShowInlineRenameInBreadcrumbs && (
							<ContentTypesInlineRenameBreadcrumb
								contentId={contentId}
								readOnly={inlineRenameReadOnly}
								contentType={contentType.type}
								spaceName={contentSpace?.name}
								title={contentBasicInfo?.title}
								isMarkdownMode={isMarkdownMode}
								shouldShowEmojiTitle={shouldShowEmojiTitle}
								onCloseTitleSpotlight={onCloseTitleSpotlight}
								registerShowTitleSpotlightHandler={registerShowTitleSpotlightHandler}
							/>
						)}
						{viewOnlyBadge}
						{!isEditPage && (
							<ContentTypesHeaderClassification
								contentStatusContext={contentStatusContext}
								contentData={contentTypesHeaderQueryNode}
								contentId={contentId}
								contentType={contentType}
								readOnly={readOnly}
								isSpaceOverviewPage={isSpaceOverviewPage}
								isObjectHeader
							/>
						)}
					</Flex>
					{!isMobile && !isMobileApp && hasActionsToRender() && (
						<Inline
							space="space.075"
							alignBlock="center"
							alignInline="end"
							xcss={[
								objectHeaderActionsContainerStyles,
								isFloatingBlockHeader && floatingBlockHeaderStyles,
							]}
							testId="object-header-actions-container"
						>
							{renderActionContainers()}
						</Inline>
					)}
				</Flex>
			</ErrorBoundary>
		);
	};

	const segmentMetric = segmentMetricsMap[contentType.type];

	const headerComponents = renderObjectHeader();

	// We moved the classification modal context provider to the fullpageeditor component level for page editor to be able to use it
	// in the page editor byline. We need to conditionally render it here for the other content types.
	const ConditionalClassificationProvider =
		contentType.mode === 'live' || contentType.mode === 'draft' || contentType.mode === 'edit'
			? Fragment
			: ClassificationModalContextProvider;
	const hasPointerEvents = !isCanvasContentType(contentType.type);

	return (
		<ErrorBoundary attribution={Attribution.CONTENT_TYPES}>
			<PageSegmentLoadStart metric={OBJECT_HEADER_SEGMENT_METRIC} />
			{segmentMetric && <PageSegmentLoadStart metric={segmentMetric} />}
			<ContentTypesHeaderContainer
				isFloating={isFloating}
				ref={setHeaderHeightElement}
				hasPointerEvents={hasPointerEvents}
				key={contentId}
			>
				<Box xcss={[flexNavigationContainerStyles, objectHeaderFlexNavigationContainerStyles]}>
					<ConditionalClassificationProvider>{headerComponents}</ConditionalClassificationProvider>
				</Box>

				{showArchiveBanner && (
					<ArchiveBannerContainer isFullWidth={hasFullWidthBanner}>
						<ArchivedPageBanner contentId={contentId} marginStyle="none" />
					</ArchiveBannerContainer>
				)}
			</ContentTypesHeaderContainer>

			<ExperienceSuccess
				name={CONTENT_TYPES_HEADER_EXPERIENCE}
				attributes={{ contentId, contentType: contentType.type }}
			/>
			<ExperienceSuccess
				name={OBJECT_HEADER_EXPERIENCE}
				attributes={{
					contentId,
					contentType: contentType.type,
					contentMode: contentType.mode,
				}}
			/>
			<PageSegmentLoadEnd
				metric={OBJECT_HEADER_SEGMENT_METRIC}
				customData={{ contentType: contentType.type, contentMode: contentType.mode }}
			/>
			{ContentTypesHeaderQueryError && <ErrorDisplay error={ContentTypesHeaderQueryError} />}
		</ErrorBoundary>
	);
};
ContentTypesHeaderInnerContent.displayName = 'ContentTypesHeaderInnerContent';

const ContentTypesHeaderInnerFromFragment: FC<
	ContentTypesHeaderProps & {
		contentFragmentRef: ContentTypesHeader_content$key;
	}
> = ({ contentFragmentRef, ...props }) => {
	const contentFragmentData = useFragment(ContentTypesHeaderFragment, contentFragmentRef);

	return <ContentTypesHeaderInnerContent {...props} contentData={contentFragmentData} />;
};
ContentTypesHeaderInnerFromFragment.displayName = 'ContentTypesHeaderInnerFromFragment';

const ContentTypesHeaderInnerFromEditorFragment: FC<
	ContentTypesHeaderProps & {
		editorContentFragmentRef: EditorContentTypesHeader_content$key | null;
	}
> = ({ editorContentFragmentRef, ...props }) => {
	const result = useFragment(EditorContentTypesHeaderFragment, editorContentFragmentRef);
	const error = useMemo(
		() =>
			result && !result.ok
				? new Error('EditorContentTypesHeader_content field error', { cause: result.errors })
				: undefined,
		[result],
	);

	return (
		<ContentTypesHeaderInnerContent
			{...props}
			contentData={result?.ok ? result.value : null}
			ContentTypesHeaderQueryError={error}
		/>
	);
};

const ContentTypesHeaderInnerFromQuery: FC<ContentTypesHeaderProps> = (props) => {
	const result = useQueryInterop<ContentTypesHeaderQueryType, ContentTypesHeaderQueryVariables>(
		ContentTypesHeaderQuery,
		{
			variables: {
				contentId: props.contentId,
			},
			fetchPolicy: 'cache-and-network',
		},
	);

	return (
		<ContentTypesHeaderInnerContent
			{...props}
			contentData={result?.data?.content?.nodes?.[0]}
			contentTypesHeaderDataLoading={result?.loading}
			ContentTypesHeaderQueryError={result?.error}
		/>
	);
};
ContentTypesHeaderInnerFromQuery.displayName = 'ContentTypesHeaderInnerFromQuery';

const ContentTypesHeaderInner: FC<ContentTypesHeaderProps> = (props) => {
	if (props.externalShareContent) {
		return <ContentTypesHeaderInnerContent {...props} contentData={props.externalShareContent} />;
	}

	if (props.editorContentFragmentRef !== undefined) {
		return (
			<ContentTypesHeaderInnerFromEditorFragment
				{...props}
				editorContentFragmentRef={props.editorContentFragmentRef}
			/>
		);
	}

	if (
		props.contentFragmentRef &&
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_obj_header_frag_dedup') &&
		expVal('confluence_exp_vp_document', 'isEnabled', false)
	) {
		return (
			<ContentTypesHeaderInnerFromFragment
				{...props}
				contentFragmentRef={props.contentFragmentRef}
			/>
		);
	}

	return <ContentTypesHeaderInnerFromQuery {...props} />;
};
ContentTypesHeaderInner.displayName = 'ContentTypesHeaderInner';

export const ContentTypesHeaderSkeleton = (): React.JSX.Element => {
	return (
		<ContentTypesHeaderContainer isFloating={false} hasPointerEvents={false}>
			<Box xcss={flexNavigationContainerStyles}>
				<Flex xcss={objectHeaderStyles}>
					<Flex xcss={objectHeaderBreadcrumbsAndMetadataStyles}>
						{/* Breadcrumbs invisible box */}
						<Box xcss={skeletonBreadcrumbsStyles} />
						{/* Title invisible box */}
						<Box xcss={skeletonTitleStyles} />
					</Flex>
					{/* Actions invisible boxes */}
					<Inline
						space="space.075"
						alignBlock="center"
						alignInline="end"
						xcss={objectHeaderActionsContainerStyles}
					>
						<Box xcss={skeletonActionSmallStyles} />
						<Box xcss={skeletonActionLargeStyles} />
					</Inline>
				</Flex>
			</Box>
		</ContentTypesHeaderContainer>
	);
};

export const ContentTypesHeader: FC<ContentTypesHeaderProps> = (props: ContentTypesHeaderProps) => {
	return (
		<SuspenseWithDataFetcher
			dataFetcherName="content-types-header"
			fallback={<ContentTypesHeaderSkeleton />}
		>
			<ContentTypesHeaderInner {...props} />
		</SuspenseWithDataFetcher>
	);
};
ContentTypesHeader.displayName = 'ContentTypesHeader';
